<template>
	<div class="otp-container">
		<div class="otp-content">
			<h1 class="otp-title">验证动态密码</h1>
      <p class="otp-hint">请输入您 MS Authenticator 应用中的 6 位验证码。</p>
			
			<div class="otp-inputs">
				<input
					v-for="(digit, index) in otpDigits"
					:key="index"
					:ref="'otpInput' + index"
					:value="otpDigits[index]"
					type="text"
					maxlength="1"
					class="otp-input-native"
					@input="handleInput(index, $event)"
					@keydown="handleKeydown(index, $event)"
					@paste="handlePaste"
				/>
			</div>
			
			<div class="otp-message">
				<el-text v-if="errorMessage" type="danger">{{ errorMessage }}</el-text>
				<el-text v-else-if="successMessage" type="success">{{ successMessage }}</el-text>
			</div>
			
			<el-button
				type="primary"
				class="otp-submit-button"
				:disabled="!isOTPComplete || loading"
				:loading="loading"
				@click="verifyOTP"
			>
				验证
			</el-button>
			
			<div class="otp-footer-text">
				<p>应用中没有显示数字？请确保您已在 MS Authenticator 应用中添加了账户。</p>
        <p>如果您还未绑定账户，请点击<a class="link-btn" @click="goToSetup">绑定用户</a>进行绑定。</p>
			</div>
		</div>
	</div>
</template>

<script>
export default {
  name: 'OtpVerify',
  props: {
    type: {
      type: String,
      default: ''
    },
    email: {
      type: String,
      default: ''
    },
    tokenObj: {
      type: Object,
      default: null
    },
    otpVerifyUrl: {
      type: String,
      default: '/coupon-system/otp/verify'
    },
    showBackBtn: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      otpDigits: ['', '', '', '', '', ''],
      loading: false,
      errorMessage: '',
      successMessage: ''
    }
  },
  computed: {
    isOTPComplete() {
      return this.otpDigits.every(digit => digit.length === 1)
    }
  },
  methods: {
    getOTPCOde() {
      return this.otpDigits.join('')
    },
    handleInput(index, event) {
      let value = event.target.value
      
      // 只允许输入数字
      if (value && !/^\d*$/.test(value)) {
        this.otpDigits.splice(index, 1, '')
        return
      }
      
      // 使用 splice 触发响应式更新
      this.otpDigits.splice(index, 1, value)
      
      // 清除错误和成功消息
      this.errorMessage = ''
      this.successMessage = ''
      
      // 如果输入了数字，自动跳到下一个输入框
      if (value && index < 5) {
        this.$nextTick(() => {
          const nextInput = this.getInputRef(index + 1)
          if (nextInput) nextInput.focus()
        })
      }
    },
    handleKeydown(index, event) {
      // 按回车键触发验证
      if (event.key === 'Enter' && this.isOTPComplete) {
        event.preventDefault()
        this.verifyOTP()
        return
      }
      
      // 按 Backspace
      if (event.key === 'Backspace') {
        // 先删除当前值
        if (this.otpDigits[index]) {
          this.otpDigits.splice(index, 1, '')
        } else if (index > 0) {
          // 当前为空，删除前一个
          this.otpDigits.splice(index - 1, 1, '')
          this.$nextTick(() => {
            const prevInput = this.getInputRef(index - 1)
            if (prevInput) prevInput.focus()
          })
        }
        event.preventDefault()
      }
      
      // 按左箭头跳到上一个输入框
      if (event.key === 'ArrowLeft' && index > 0) {
        const prevInput = this.getInputRef(index - 1)
        if (prevInput) prevInput.focus()
      }
      
      // 按右箭头跳到下一个输入框
      if (event.key === 'ArrowRight' && index < 5) {
        const nextInput = this.getInputRef(index + 1)
        if (nextInput) nextInput.focus()
      }
    },
    handlePaste(event) {
      event.preventDefault()
      const pastedText = event.clipboardData ? event.clipboardData.getData('text') : ''
      
      // 提取数字
      const digits = pastedText.replace(/\D/g, '').slice(0, 6)
      
      // 填充输入框
      for (let i = 0; i < digits.length; i++) {
        this.otpDigits[i] = digits[i]
      }
      
      // 聚焦到下一个空输入框或最后一个
      const nextEmptyIndex = this.otpDigits.findIndex(d => !d)
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(digits.length, 5)
      this.$nextTick(() => {
        const focusInput = this.getInputRef(focusIndex)
        if (focusInput) focusInput.focus()
      })
    },
    getInputRef(index) {
      const ref = this.$refs['otpInput' + index]
      if (ref && ref[0]) return ref[0]
      return ref
    },
    verifyOTP() {
      if (!this.isOTPComplete) {
        return
      }
      
      this.loading = true
      this.errorMessage = ''
      this.successMessage = ''
      
      const otpCode = this.getOTPCOde()
      const data = {
        code: otpCode,
        userid: ''
      }
      if (this.type === 'login') {
        // 从 props 中获取 tokenObj
        data.userid = this.tokenObj ? this.tokenObj.id : ''
      }
      
      const _this = this
      // 调用 OTP 验证接口
      this.$ajax({
        url: this.otpVerifyUrl,
        methods: 'POST',
        data: data,
        token: window.sessionStorage.getItem('token'),
        success: function(response) {
          console.log('res:::::', response)
          _this.loading = false
          
          if (response.code === 0) {
            // 验证成功
            _this.successMessage = '验证成功'
            // 使用 props 中的 tokenObj 作为用户信息
            _this.$emit('verify-success', otpCode, { data: _this.tokenObj })
          } else {
            // 验证失败
            _this.errorMessage = response.message || '验证码验证失败'
            _this.$emit('verify-fail', response)
          }
        },
        error: function(error) {
          _this.loading = false
          _this.errorMessage = '验证码验证失败'
          _this.$emit('verify-fail', error)
        }
      })
    },
    clearOTP() {
      this.otpDigits = ['', '', '', '', '', '']
      this.errorMessage = ''
      this.successMessage = ''
      this.$nextTick(() => {
        const firstInput = this.getInputRef(0)
        if (firstInput) firstInput.focus()
      })
    },
    focusFirstInput() {
      this.$nextTick(() => {
        const firstInput = this.getInputRef(0)
        if (firstInput) firstInput.focus()
      })
    },
    focusInput() {
      this.focusFirstInput()
    },
    goToSetup() {
      this.$emit('go-to-setup')
    },
    setLoading(value) {
      this.loading = value
    },
    setErrorMessage(message) {
      this.errorMessage = message
      this.loading = false
    },
    setSuccessMessage(message) {
      this.successMessage = message
      this.loading = false
    }
  },
  mounted() {
    this.focusFirstInput()
  }
}
</script>

<style scoped>
.otp-container {
  display: flex;
  align-items: center;
  justify-content: center;
  /* padding: 20px; */
  overflow-y: auto;
}

.otp-content {
  position: relative;
  width: 100%;
  max-width: 480px;
  text-align: center;
  max-height: 100vh;
  overflow-y: auto;
  padding: 10px 0;
}

.otp-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #303133;
}

.otp-hint {
  font-size: 14px;
  color: #606266;
  margin: 0 0 32px 0;
  line-height: 1.6;
}

.otp-inputs {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 16px;
}

.otp-input-native {
  width: 40px;
  height: 40px;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s;
}

.otp-input-native:focus {
  border-color: #409EFF;
}

.otp-message {
  min-height: 24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.otp-submit-button {
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 32px;
}

.otp-footer-text {
  font-size: 12px;
  color: #909399;
  line-height: 1.8;
  text-align: left;
}

.otp-footer-text p {
  margin: 0 0 8px 0;
}

.otp-footer-text p:last-child {
  margin-bottom: 0;
}

.link-btn {
  color: #409EFF;
  cursor: pointer;
  text-decoration: underline;
}

.link-btn:hover {
  color: #66b1ff;
}

</style>


// WEBPACK FOOTER //
// src/components/verifyOTP.vue